import { useLocation } from 'react-router';

import { RoccoLink } from '@rocco/components/link';
import { ProfessionalTileLogo } from '@rocco/ui/tile-professional/components/ProfessionalTileLogo';
import { HeroCrossfade } from '~/ui-rocco/pages/detail-shared/DetailHero';
import { useProfessionalLayoutData } from '@modules/professional/hook/use-professional-layout-data';
import { professionalTabBase } from '@modules/professional/util/professional-tab-base';

/**
 * Social-profile hero (mobile) — a 16:9 full-bleed banner that cross-fades
 * between the professional's banner images (matching the desktop hero carousel),
 * with the company logo badge ringed in white and overlapping its bottom-left
 * edge. The name, stats and CTA sit below on white in
 * {@link import('./ProfessionalProfileHeader')}.
 *
 * Reads the shared layout loader and lives in the persistent layout, so it
 * renders once and never re-flashes across tab navigation.
 */
export const ProfessionalProfileHero = () => {
    const layoutData = useProfessionalLayoutData();
    const { pathname } = useLocation();
    if (!layoutData) return null;
    const { initial, banners } = layoutData;

    const bannerImages = banners
        .map((banner) => banner.libraryImageFile?.DetailBannerStandard?.Desktop)
        .filter((src): src is string => Boolean(src));

    return (
        <section className="relative w-full bg-ground">
            <div className="bg-neutral-200 relative aspect-video w-full overflow-hidden">
                <HeroCrossfade images={bannerImages} />
                {/* The transparent header's white marks sit on this
                    photograph; on a bright sky they vanished. The masthead
                    scrim carries them, as the article and directory heroes
                    carry theirs. */}
                <div
                    aria-hidden="true"
                    className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-gradient-to-b from-[#131313]/45 to-transparent"
                />
            </div>
            {initial.CompanyLogo ? (
                <RoccoLink
                    // The profile's own tab base: initial.Link is the public
                    // path, which walks the Business Manager preview out onto
                    // the live site.
                    to={professionalTabBase(initial.URLSegment, pathname)}
                    aria-label={`${initial.Title} logo`}
                    className="absolute -bottom-10 left-6 z-10 block size-24 rounded-full border-[3px] border-white bg-white shadow-[0_2px_10px_rgba(0,0,0,0.12)]"
                >
                    <ProfessionalTileLogo
                        image={{
                            src: initial.CompanyLogo,
                            alt: `${initial.Title} logo`,
                        }}
                        background={initial.LogoBackgroundColor ?? null}
                        className="size-full"
                        contrastBorder
                    />
                </RoccoLink>
            ) : null}
        </section>
    );
};
